"use client";

import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Bot,
  Calendar,
  CheckCircle2,
  CircleDot,
  Clock3,
  ExternalLink,
  FileText,
  GitBranch,
  GitCommitHorizontal,
  ListTodo,
  Loader2,
  Plus,
  Radio,
  Sparkles,
} from "lucide-react";
import { useStore } from "@/lib/store";
import { PROJECT_STATUSES } from "@/lib/constants";
import {
  deriveProjectInsights,
  type ProjectHealthKind,
} from "@/lib/projectInsights";
import {
  useProjectCommandData,
  type ProjectActivityRow,
} from "@/lib/useProjectCommandData";
import type {
  AgentRun,
  Issue,
  ProgressCard,
  ProgressSourceRef,
  Project,
  ProjectMilestone,
} from "@/lib/types";
import { cn, formatDate, formatRelative } from "@/lib/utils";
import { StatusIcon } from "@/components/ui/StatusIcon";
import { PriorityIcon } from "@/components/ui/PriorityIcon";
import { Avatar } from "@/components/ui/Avatar";
import { ListView } from "@/components/issues/ListView";
import { safeExternalUrl } from "@/lib/externalUrl";
import { ProjectSlackUpdates } from "@/components/slack/ProjectSlackUpdates";
import { ProjectMilestones } from "./ProjectMilestones";
import { docIdentifier } from "@/lib/docIdentity";

type ProjectTab = "overview" | "work" | "timeline";
type TimelineSource = "all" | "pauta" | "git" | "agent";

const PROJECT_TABS: Array<{ id: ProjectTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "work", label: "Tasks" },
  { id: "timeline", label: "Timeline" },
];

export function ProjectCommandCenter({
  projectId,
  onBack,
  onNavigate,
  onOpenAsk,
  canWrite,
}: {
  projectId: string;
  onBack: () => void;
  onNavigate: (view: string) => void;
  onOpenAsk: () => void;
  canWrite: boolean;
}) {
  const activeWorkspaceId = useStore((state) => state.activeWorkspaceId);
  const project = useStore((state) =>
    state.projects.find(
      (candidate) =>
        candidate.id === projectId && candidate.workspaceId === activeWorkspaceId
    )
  );
  const issues = useStore((state) => state.issues);
  const progressCards = useStore((state) => state.progressCards);
  const docs = useStore((state) => state.docs);
  const agentRuns = useStore((state) => state.agentRuns);
  const updateProject = useStore((state) => state.updateProject);
  const setCreateOpen = useStore((state) => state.setCreateOpen);
  const selectProgressCard = useStore((state) => state.selectProgressCard);
  const [tab, setTab] = useState<ProjectTab>("overview");

  const commandData = useProjectCommandData({
    workspaceId: project?.workspaceId ?? "",
    projectId,
  });

  const insight = useMemo(
    () =>
      project
        ? deriveProjectInsights({ project, issues, progressCards, docs, agentRuns })
        : null,
    [agentRuns, docs, issues, progressCards, project]
  );

  if (!project || !insight) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center">
          <p className="text-[13px] font-medium text-[var(--fg)]">Project not found</p>
          <button
            type="button"
            onClick={onBack}
            className="mt-2 text-[12px] text-[var(--accent)] hover:text-[var(--fg)] transition-colors"
          >
            Return to projects
          </button>
        </div>
      </div>
    );
  }

  function openProgressCard(card: ProgressCard) {
    selectProgressCard(card.id);
    onNavigate("progress");
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-[var(--border)] bg-[var(--bg-secondary)]">
        <div className="mx-auto w-full max-w-[1280px] px-4 pt-4 md:px-6">
          <button
            type="button"
            onClick={onBack}
            className="mb-3 inline-flex items-center gap-1.5 rounded px-1 py-1 text-[11.5px] text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] transition-colors"
          >
            <ArrowLeft size={12} /> Projects
          </button>

          <div className="flex flex-col gap-4 pb-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 gap-3">
              <div
                aria-hidden="true"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border text-[20px]"
                style={{
                  color: project.color,
                  borderColor: `${project.color}66`,
                  background: `${project.color}18`,
                }}
              >
                {project.icon}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="truncate text-[20px] font-medium tracking-tight text-[var(--fg)]">
                    {project.name}
                  </h1>
                  <HealthChip kind={insight.health.kind} label={insight.health.label} />
                </div>
                <p className="mt-0.5 max-w-3xl text-[12px] text-[var(--fg-tertiary)]">
                  {project.description || "No project brief yet."}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {commandData.sources.map((source) => {
                    const href = safeExternalUrl(source.url);
                    const content = (
                      <>
                        <GitBranch size={10} />
                        {source.repository}
                        {href && <ExternalLink size={9} className="text-[var(--fg-muted)]" />}
                      </>
                    );
                    const className =
                      "inline-flex items-center gap-1.5 rounded border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1 text-[10.5px] text-[var(--fg-secondary)]";
                    return href ? (
                      <a
                        key={source.id}
                        href={href}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={`${className} hover:bg-[var(--bg-hover)] transition-colors`}
                      >
                        {content}
                      </a>
                    ) : (
                      <span
                        key={source.id}
                        className={`${className} opacity-60`}
                        title="Repository URL is unavailable or unsafe"
                      >
                        {content}
                      </span>
                    );
                  })}
                  {commandData.sources.length === 0 && (
                    <span className="inline-flex items-center gap-1 text-[10.5px] text-[var(--fg-muted)]">
                      <GitBranch size={10} /> No repository mapped
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <button
                type="button"
                onClick={onOpenAsk}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 text-[11.5px] font-medium text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] transition-colors"
              >
                <Sparkles size={12} className="text-[var(--accent)]" />
                Ask this project
              </button>
              {canWrite ? (
                <>
                  <select
                    aria-label="Project status"
                    value={project.status}
                    onChange={(event) =>
                      updateProject(project.id, {
                        status: event.target.value as Project["status"],
                      })
                    }
                    className="h-8 rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 text-[11.5px] text-[var(--fg-secondary)] outline-none hover:bg-[var(--bg-hover)] transition-colors"
                  >
                    {PROJECT_STATUSES.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                  <DateField
                    label="Start"
                    value={project.startDate}
                    onChange={(startDate) => updateProject(project.id, { startDate })}
                  />
                  <DateField
                    label="Target"
                    value={project.targetDate}
                    onChange={(targetDate) => updateProject(project.id, { targetDate })}
                  />
                  <button
                    type="button"
                    onClick={() => setCreateOpen(true)}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 text-[11.5px] font-medium text-white hover:bg-[var(--accent-hover)] transition-colors"
                  >
                    <Plus size={12} /> Add task
                  </button>
                </>
              ) : (
                <>
                  <ReadOnlyMeta value={PROJECT_STATUSES.find((status) => status.id === project.status)?.name ?? project.status} />
                  <ReadOnlyDate label="Start" value={project.startDate} />
                  <ReadOnlyDate label="Target" value={project.targetDate} />
                </>
              )}
            </div>
          </div>

          <nav
            className="flex items-center gap-1"
            role="tablist"
            aria-label={`${project.name} sections`}
          >
            {PROJECT_TABS.map((item, index) => (
              <TabButton
                key={item.id}
                id={`project-${project.id}-tab-${item.id}`}
                controls={`project-${project.id}-panel-${item.id}`}
                active={tab === item.id}
                onClick={() => setTab(item.id)}
                onKeyDown={(event) => {
                  if (
                    event.key !== "ArrowLeft" &&
                    event.key !== "ArrowRight" &&
                    event.key !== "Home" &&
                    event.key !== "End"
                  ) {
                    return;
                  }
                  event.preventDefault();
                  const next =
                    event.key === "Home"
                      ? PROJECT_TABS[0]
                      : event.key === "End"
                        ? PROJECT_TABS[PROJECT_TABS.length - 1]
                        : PROJECT_TABS[
                            (index +
                              (event.key === "ArrowRight" ? 1 : -1) +
                              PROJECT_TABS.length) %
                              PROJECT_TABS.length
                          ];
                  setTab(next.id);
                  requestAnimationFrame(() =>
                    document
                      .getElementById(
                        `project-${project.id}-tab-${next.id}`
                      )
                      ?.focus()
                  );
                }}
                count={
                  item.id === "work" ? insight.counts.total : undefined
                }
              >
                {item.label}
              </TabButton>
            ))}
          </nav>
        </div>
      </div>

      <div
        role="tabpanel"
        tabIndex={0}
        id={`project-${project.id}-panel-${tab}`}
        aria-labelledby={`project-${project.id}-tab-${tab}`}
        className="focus-ring flex min-h-0 flex-1 flex-col"
      >
        {tab === "overview" ? (
          <ProjectOverview
            project={project}
            insight={insight}
            milestones={commandData.milestones}
            onCreateMilestone={commandData.createMilestone}
            onUpdateMilestone={commandData.updateMilestone}
            onDeleteMilestone={commandData.deleteMilestone}
            onOpenProgress={openProgressCard}
            onOpenTimeline={() => setTab("timeline")}
            canWrite={canWrite}
          />
        ) : tab === "work" ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <ListView issues={insight.projectIssues} canWrite={canWrite} />
          </div>
        ) : (
          <ProjectTimeline
            project={project}
            insight={insight}
            milestones={commandData.milestones}
            activity={commandData.activity}
            loading={commandData.loading}
          />
        )}
      </div>
    </div>
  );
}

function ProjectOverview({
  project,
  insight,
  milestones,
  onCreateMilestone,
  onUpdateMilestone,
  onDeleteMilestone,
  onOpenProgress,
  onOpenTimeline,
  canWrite,
}: {
  project: Project;
  insight: ReturnType<typeof deriveProjectInsights>;
  milestones: ProjectMilestone[];
  onCreateMilestone: (input: {
    title: string;
    description?: string;
    targetDate: number;
    status?: ProjectMilestone["status"];
    linkedIssueIds?: string[];
  }) => Promise<ProjectMilestone>;
  onUpdateMilestone: (id: string, patch: Partial<ProjectMilestone>) => Promise<ProjectMilestone>;
  onDeleteMilestone: (id: string) => Promise<void>;
  onOpenProgress: (card: ProgressCard) => void;
  onOpenTimeline: () => void;
  canWrite: boolean;
}) {
  const latest = insight.latestProgressCard;
  const latestPublished = insight.projectProgressCards.find(
    (card) => card.status === "published"
  ) ?? null;

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      <div className="mx-auto w-full max-w-[1280px] px-4 py-5 md:px-6">
        <div className="mb-4 grid grid-cols-2 gap-px overflow-hidden rounded-md border border-[var(--border)] bg-[var(--border)] md:grid-cols-5">
          <Metric label="Doing" value={insight.counts.doing} />
          <Metric label="In review" value={insight.counts.review} />
          <Metric label="Next" value={insight.counts.todo} />
          <Metric label="Done · 7d" value={insight.counts.doneLast7d} tone="success" />
          <Metric
            label="Attention"
            value={insight.attentionCount}
            tone={insight.attentionCount ? "danger" : "default"}
          />
        </div>

        {insight.attentionReasons.length > 0 && (
          <div
            className="mb-4 rounded-md border px-3 py-3"
            style={{
              borderColor: "color-mix(in srgb, var(--red) 36%, var(--border))",
              background: "color-mix(in srgb, var(--red) 7%, var(--bg-secondary))",
            }}
          >
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[var(--red)]" />
              <div>
                <p className="text-[12px] font-medium text-[var(--fg)]">Needs attention</p>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                  {insight.attentionReasons.map((reason) => (
                    <span key={reason.kind} className="text-[11.5px] text-[var(--fg-secondary)]">
                      {reason.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mb-4">
          <Section
            title="Goals & deadlines"
            icon={<Calendar size={12} className="text-[var(--blue)]" />}
            count={milestones.length}
          >
            <ProjectMilestones
              milestones={milestones}
              issues={insight.projectIssues}
              canWrite={canWrite}
              onCreate={(input) => onCreateMilestone(input)}
              onUpdate={onUpdateMilestone}
              onDelete={onDeleteMilestone}
            />
          </Section>
        </div>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.7fr)_minmax(300px,0.8fr)]">
          <div className="space-y-4">
            <Section title="Current pulse" icon={<Radio size={12} className="text-[var(--green)]" />}>
              {latest ? (
                <div className="w-full rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-4 text-left">
                  <div className="flex flex-wrap items-center gap-2 text-[10.5px] text-[var(--fg-muted)]">
                    <SourceChip source={latest.source} />
                    <span>{formatDate(latest.periodStart)} – {formatDate(latest.periodEnd)}</span>
                    <span>Updated {formatRelative(latest.updatedAt)}</span>
                    <button
                      type="button"
                      onClick={() => onOpenProgress(latest)}
                      className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-1 text-[10.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] transition-colors"
                    >
                      Open pulse <ArrowRight size={11} />
                    </button>
                  </div>
                  <h3 className="mt-2 text-[13.5px] font-medium text-[var(--fg)]">{latest.title}</h3>
                  <p className="mt-1 text-[12px] leading-relaxed text-[var(--fg-secondary)]">
                    {latest.summary || "No summary was recorded for this pulse."}
                  </p>
                  {(latest.highlights.length > 0 || latest.blockers.length > 0) && (
                    <div className="mt-3 grid gap-3 border-t border-[var(--border)] pt-3 md:grid-cols-2">
                      <SignalList
                        icon={<CheckCircle2 size={11} className="text-[var(--green)]" />}
                        label="Highlights"
                        values={latest.highlights.slice(0, 3)}
                      />
                      <SignalList
                        icon={<AlertTriangle size={11} className="text-[var(--orange)]" />}
                        label="Blockers"
                        values={latest.blockers.slice(0, 3)}
                      />
                    </div>
                  )}
                  {latest.sourceRefs.length > 0 && (
                    <EvidenceLinks refs={latest.sourceRefs.slice(0, 4)} />
                  )}
                </div>
              ) : (
                <EmptyPanel
                  icon={<Sparkles size={16} />}
                  title="No project pulse yet"
                  body="Git sync or a progress card will turn repository activity into a concise team update."
                />
              )}
            </Section>

            <div className="grid gap-4 lg:grid-cols-2">
              <Section title="Current focus" count={insight.currentFocus.length}>
                <IssueStack issues={insight.currentFocus.slice(0, 5)} empty="No work is actively moving." />
              </Section>
              <Section title="In review" count={insight.review.length}>
                <IssueStack issues={insight.review.slice(0, 5)} empty="Nothing is waiting for review." />
              </Section>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Section title="Recent outcomes" count={insight.recentOutcomes.length}>
                <IssueStack issues={insight.recentOutcomes.slice(0, 5)} empty="No completed outcomes in the last 7 days." />
              </Section>
              <Section title="Next up" count={insight.next.length}>
                <IssueStack issues={insight.next.slice(0, 5)} empty="No committed work is queued next." />
              </Section>
            </div>
          </div>

          <div className="space-y-4">
            <Section title="Project signal">
              <div className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-4">
                <HealthChip kind={insight.health.kind} label={insight.health.label} />
                <p className="mt-2 text-[12px] leading-relaxed text-[var(--fg-secondary)]">
                  {insight.health.reason}
                </p>
                <div className="mt-3 border-t border-[var(--border)] pt-3 text-[11px] text-[var(--fg-muted)]">
                  Last signal {formatRelative(insight.latestSignalAt)} · {signalLabel(insight.latestSignal.source)}
                </div>
              </div>
            </Section>

            <ProjectSlackUpdates
              workspaceId={project.workspaceId}
              projectId={project.id}
              latestPublishedCard={latestPublished}
              canWrite={canWrite}
            />

            <Section title="Plan">
              <div className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-4">
                <PlanRow label="Start" value={project.startDate ? formatDate(project.startDate) : "Needs date"} />
                <PlanRow label="Target" value={project.targetDate ? formatDate(project.targetDate) : "Needs date"} />
                <PlanRow label="Committed" value={`${insight.counts.done}/${insight.counts.committed} complete`} />
                <div
                  role="progressbar"
                  aria-label="Project completion"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={insight.counts.completionPercent}
                  className="mt-3 h-1.5 overflow-hidden rounded-full bg-[var(--bg-tertiary)]"
                >
                  <span
                    className="block h-full rounded-full bg-[var(--green)]"
                    style={{ width: `${insight.counts.completionPercent}%` }}
                  />
                </div>
                <p className="mt-1.5 text-right text-[10.5px] text-[var(--fg-muted)]">
                  {insight.counts.completionPercent}% of committed work
                </p>
              </div>
            </Section>

            <Section title="Connected context">
              <div className="overflow-hidden rounded-md border border-[var(--border)] bg-[var(--bg-secondary)]">
                <ContextRow icon={<FileText size={12} />} label="Project docs" value={insight.projectDocs.length} />
                <ContextRow icon={<Radio size={12} />} label="Progress pulses" value={insight.projectProgressCards.length} />
                <ContextRow icon={<Bot size={12} />} label="Agent runs" value={insight.scopedAgentRuns.length} />
              </div>
              <button
                type="button"
                onClick={onOpenTimeline}
                className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-medium text-[var(--accent)] hover:text-[var(--fg)] transition-colors"
              >
                See complete timeline <ArrowRight size={11} />
              </button>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectTimeline({
  project,
  insight,
  milestones,
  activity,
  loading,
}: {
  project: Project;
  insight: ReturnType<typeof deriveProjectInsights>;
  milestones: ProjectMilestone[];
  activity: ProjectActivityRow[];
  loading: boolean;
}) {
  const users = useStore((state) => state.users);
  const selectIssue = useStore((state) => state.selectIssue);
  const [source, setSource] = useState<TimelineSource>("all");

  const events = useMemo(
    () => buildTimeline(project, insight, milestones, activity),
    [activity, insight, milestones, project]
  );
  const visible = source === "all" ? events : events.filter((event) => event.source === source);
  const groups = groupTimeline(visible);

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      <div className="mx-auto w-full max-w-[980px] px-4 py-5 md:px-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-[15px] font-medium text-[var(--fg)]">Project timeline</h3>
            <p className="mt-0.5 text-[11.5px] text-[var(--fg-tertiary)]">
              Task decisions, Git delivery evidence, and agent execution in one ordered view.
            </p>
          </div>
          <div
            role="group"
            aria-label="Filter timeline by source"
            className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-1"
          >
            {(["all", "pauta", "git", "agent"] as TimelineSource[]).map((item) => (
              <button
                key={item}
                type="button"
                aria-pressed={source === item}
                onClick={() => setSource(item)}
                className={cn(
                  "rounded px-2 py-1 text-[10.5px] font-medium capitalize transition-colors",
                  source === item
                    ? "bg-[var(--bg-active)] text-[var(--fg)]"
                    : "text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)]"
                )}
              >
                {item === "git" ? "Git" : item}
              </button>
            ))}
          </div>
        </div>

        {loading && events.length === 0 ? (
          <div className="flex min-h-[280px] items-center justify-center text-[12px] text-[var(--fg-muted)]">
            <Loader2 size={13} className="mr-2 animate-spin" /> Loading project evidence…
          </div>
        ) : groups.length === 0 ? (
          <EmptyPanel
            icon={<Clock3 size={16} />}
            title="No timeline signals yet"
            body="Task movement, project pulses, Git evidence, and agent runs will appear here automatically."
          />
        ) : (
          <div className="space-y-6">
            {groups.map((group) => (
              <section key={group.label}>
                <div className="sticky top-0 z-10 mb-2 bg-[var(--bg)] py-1 text-[10.5px] font-medium uppercase tracking-wider text-[var(--fg-muted)]">
                  {group.label}
                </div>
                <div className="overflow-hidden rounded-md border border-[var(--border)] bg-[var(--bg-secondary)]">
                  {group.events.map((event) => {
                    const actor = users.find((user) => user.id === event.actorId);
                    return (
                      <div
                        key={event.id}
                        className="flex gap-3 border-b border-[var(--border)] px-3 py-3 last:border-b-0"
                      >
                        <TimelineIcon source={event.source} kind={event.kind} />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                            {event.issueId ? (
                              <button
                                type="button"
                                onClick={() => selectIssue(event.issueId!)}
                                className="focus-ring min-w-0 rounded text-left text-[12px] font-medium text-[var(--fg)] transition-colors hover:text-[var(--accent)]"
                              >
                                {event.title}
                              </button>
                            ) : (
                              <span className="min-w-0 text-[12px] font-medium text-[var(--fg)]">
                                {event.title}
                              </span>
                            )}
                            <SourceChip source={event.source === "git" ? "github" : event.source === "agent" ? "agent" : "manual"} />
                            <span className="ml-auto whitespace-nowrap text-[10.5px] text-[var(--fg-muted)]">
                              {formatRelative(event.at)}
                            </span>
                          </div>
                          {event.detail && (
                            <p className="mt-1 line-clamp-2 text-[11.5px] leading-relaxed text-[var(--fg-tertiary)]">
                              {event.detail}
                            </p>
                          )}
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-[10.5px] text-[var(--fg-muted)]">
                            {actor && (
                              <span className="inline-flex items-center gap-1">
                                <Avatar user={actor} size={15} /> {actor.name}
                              </span>
                            )}
                            <span>{event.entityLabel}</span>
                          </div>
                          {event.refs.length > 0 && <EvidenceLinks refs={event.refs.slice(0, 5)} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface TimelineEvent {
  id: string;
  at: number;
  source: Exclude<TimelineSource, "all">;
  kind: "work" | "delivery" | "update" | "knowledge" | "agent";
  title: string;
  detail: string;
  actorId: string | null;
  entityLabel: string;
  issueId: string | null;
  refs: ProgressSourceRef[];
}

function buildTimeline(
  project: Project,
  insight: ReturnType<typeof deriveProjectInsights>,
  milestones: ProjectMilestone[],
  activity: ProjectActivityRow[]
): TimelineEvent[] {
  const issues = new Map(insight.projectIssues.map((issue) => [issue.id, issue]));
  const docs = new Map(insight.projectDocs.map((doc) => [doc.id, doc]));
  const milestoneById = new Map(milestones.map((milestone) => [milestone.id, milestone]));

  const pulseEvents: TimelineEvent[] = insight.projectProgressCards.map((card) => ({
    id: `pulse:${card.id}`,
    at: card.updatedAt,
    source: card.source === "github" ? "git" : card.source === "agent" ? "agent" : "pauta",
    kind: card.source === "github" ? "delivery" : "update",
    title: card.title,
    detail: card.summary,
    actorId: card.authorId,
    entityLabel: card.source === "github" ? "Git project pulse" : "Progress card",
    issueId: null,
    refs: card.sourceRefs,
  }));

  const runEvents: TimelineEvent[] = insight.scopedAgentRuns.map((run) => ({
    id: `run:${run.id}`,
    at: run.endedAt ?? run.createdAt,
    source: "agent",
    kind: "agent",
    title: run.summary || compactPrompt(run),
    detail: run.actions.slice(0, 3).join(" · "),
    actorId: run.actorId,
    entityLabel: `Agent run · ${run.status}`,
    issueId: run.touchedIssueIds.find((id) => issues.has(id)) ?? null,
    refs: [],
  }));

  const activityEvents: TimelineEvent[] = activity
    .filter((row) => row.entity_type !== "progress_card" && row.entity_type !== "agent_run")
    .map((row) => {
      const issue = row.entity_type === "issue" ? issues.get(row.entity_id) : undefined;
      const doc = row.entity_type === "doc" ? docs.get(row.entity_id) : undefined;
      const milestone =
        row.entity_type === "project_milestone"
          ? milestoneById.get(row.entity_id)
          : undefined;
      const title = issue
        ? `${issue.identifier} · ${issue.title}`
        : doc
          ? (docIdentifier(doc) ? `${docIdentifier(doc)} · ${doc.title}` : doc.title)
          : milestone
            ? row.entity_identifier || milestone.title
          : row.entity_type === "project"
            ? project.name
            : row.entity_identifier || titleCase(row.entity_type);
      return {
        id: `activity:${row.id}`,
        at: new Date(row.created_at).getTime(),
        source: "pauta" as const,
        kind:
          row.entity_type === "doc"
            ? ("knowledge" as const)
            : row.entity_type === "project_milestone"
              ? ("update" as const)
              : ("work" as const),
        title,
        detail: activityDescription(row.action, row.entity_type),
        actorId: row.actor_id,
        entityLabel:
          row.entity_type === "project_milestone"
            ? "Project goal"
            : entityDisplayName(row.entity_type),
        issueId:
          issue?.id ?? milestone?.linkedIssueIds.find((id) => issues.has(id)) ?? null,
        refs: [],
      };
    });

  return [...pulseEvents, ...runEvents, ...activityEvents].sort(
    (a, b) => b.at - a.at
  );
}

function groupTimeline(events: TimelineEvent[]) {
  const now = Date.now();
  const startToday = startOfDay(now);
  const startWeek = startToday - 6 * 24 * 60 * 60 * 1000;
  const buckets = [
    { label: "Today", events: [] as TimelineEvent[] },
    { label: "This week", events: [] as TimelineEvent[] },
    { label: "Earlier", events: [] as TimelineEvent[] },
  ];
  for (const event of events) {
    if (event.at >= startToday) buckets[0].events.push(event);
    else if (event.at >= startWeek) buckets[1].events.push(event);
    else buckets[2].events.push(event);
  }
  return buckets.filter((bucket) => bucket.events.length > 0);
}

function IssueStack({ issues, empty }: { issues: Issue[]; empty: string }) {
  const selectIssue = useStore((state) => state.selectIssue);
  const users = useStore((state) => state.users);
  if (issues.length === 0) {
    return <p className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-4 text-[11.5px] text-[var(--fg-muted)]">{empty}</p>;
  }
  return (
    <div className="overflow-hidden rounded-md border border-[var(--border)] bg-[var(--bg-secondary)]">
      {issues.map((issue) => {
        const owner = users.find(
          (user) => user.id === (issue.ownerId ?? null)
        );
        const executor = users.find(
          (user) => user.id === (issue.executorAgentId ?? null)
        );
        const legacyAssignee =
          !owner && !executor
            ? users.find((user) => user.id === issue.assigneeId)
            : undefined;
        return (
          <button
            key={issue.id}
            type="button"
            onClick={() => selectIssue(issue.id)}
            className="focus-ring flex w-full items-center gap-2 border-b border-[var(--border)] px-3 py-2.5 text-left last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors"
          >
            <StatusIcon status={issue.status} size={12} />
            <PriorityIcon priority={issue.priority} size={11} />
            <span className="w-[58px] shrink-0 font-mono text-[10.5px] text-[var(--fg-muted)]">
              {issue.identifier}
            </span>
            <span className="min-w-0 flex-1 truncate text-[11.5px] text-[var(--fg-secondary)]">
              {issue.title}
            </span>
            {(owner || legacyAssignee) && (
              <span
                aria-hidden="true"
                title={`Owner: ${(owner ?? legacyAssignee)?.name}`}
              >
                <Avatar user={owner ?? legacyAssignee} size={16} />
              </span>
            )}
            {executor && (
              <span
                aria-hidden="true"
                className="inline-flex items-center gap-0.5"
                title={`Agent: ${executor.name}`}
              >
                <Bot size={9} className="text-[var(--brand-indigo)]" />
                <Avatar user={executor} size={16} />
              </span>
            )}
            <span className="sr-only">
              {owner || legacyAssignee
                ? `Owner ${(owner ?? legacyAssignee)?.name}. `
                : "No owner. "}
              {executor ? `Agent ${executor.name}.` : "No agent."}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Section({
  title,
  icon,
  count,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-1.5 text-[10.5px] font-medium uppercase tracking-wider text-[var(--fg-muted)]">
        {icon}
        <span>{title}</span>
        {count !== undefined && <span className="font-normal normal-case tracking-normal">{count}</span>}
      </div>
      {children}
    </section>
  );
}

function Metric({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "danger" | "success";
}) {
  return (
    <div className="bg-[var(--bg-secondary)] px-4 py-3">
      <div className="text-[10px] font-medium uppercase tracking-wider text-[var(--fg-muted)]">{label}</div>
      <div
        className={cn(
          "mt-1 text-[19px] font-medium tracking-tight",
          tone === "danger" ? "text-[var(--red)]" : tone === "success" ? "text-[var(--green)]" : "text-[var(--fg)]"
        )}
      >
        {value}
      </div>
    </div>
  );
}

function TabButton({
  id,
  controls,
  active,
  onClick,
  onKeyDown,
  count,
  children,
}: {
  id: string;
  controls: string;
  active: boolean;
  onClick: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      aria-controls={controls}
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={cn(
        "focus-ring relative inline-flex h-9 items-center gap-1.5 px-3 text-[11.5px] font-medium transition-colors",
        active ? "text-[var(--fg)]" : "text-[var(--fg-tertiary)] hover:text-[var(--fg)]"
      )}
    >
      {children}
      {count !== undefined && <span className="text-[10px] text-[var(--fg-muted)]">{count}</span>}
      {active && (
        <span
          aria-hidden="true"
          className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[var(--accent)]"
        />
      )}
    </button>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  return (
    <label className="flex h-8 items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 text-[10.5px] text-[var(--fg-muted)] hover:bg-[var(--bg-hover)] transition-colors">
      <Calendar size={10} /> {label}
      <input
        type="date"
        aria-label={`${label} date`}
        value={value ? toDateInput(value) : ""}
        onChange={(event) => onChange(event.target.value ? parseDateInput(event.target.value) : null)}
        className="w-[104px] bg-transparent text-[10.5px] text-[var(--fg-secondary)] outline-none"
      />
    </label>
  );
}

function ReadOnlyMeta({ value }: { value: string }) {
  return (
    <span className="inline-flex h-8 items-center rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 text-[11.5px] text-[var(--fg-secondary)]">
      {value}
    </span>
  );
}

function ReadOnlyDate({ label, value }: { label: string; value: number | null }) {
  return (
    <span className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 text-[10.5px] text-[var(--fg-muted)]">
      <Calendar size={10} /> {label}
      <span className="text-[var(--fg-secondary)]">{value ? formatDate(value) : "Not set"}</span>
    </span>
  );
}

function HealthChip({ kind, label }: { kind: ProjectHealthKind; label: string }) {
  const tone = HEALTH_TONES[kind];
  return (
    <span
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium"
      style={{ color: tone, background: `color-mix(in srgb, ${tone} 14%, transparent)` }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone }} />
      {label}
    </span>
  );
}

function SourceChip({ source }: { source: ProgressCard["source"] }) {
  const label = source === "github" ? "Git" : source === "agent" ? "Agent" : source === "slack" ? "Slack" : "Pauta";
  const Icon = source === "github" ? GitCommitHorizontal : source === "agent" ? Bot : CircleDot;
  return (
    <span className="inline-flex items-center gap-1 rounded bg-[var(--bg-tertiary)] px-1.5 py-0.5 text-[9.5px] font-medium text-[var(--fg-tertiary)]">
      <Icon size={9} /> {label}
    </span>
  );
}

function EvidenceLinks({ refs }: { refs: ProgressSourceRef[] }) {
  return (
    <div className="mt-3 flex flex-wrap gap-1.5 border-t border-[var(--border)] pt-3">
      {refs.map((ref, index) => {
        const href = safeExternalUrl(ref.url);
        const content = (
          <>
            <GitBranch size={9} /> <span className="truncate">{ref.label}</span>
            {href && <ExternalLink size={8} />}
          </>
        );
        const className =
          "inline-flex max-w-[260px] items-center gap-1 rounded border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1 text-[10px] text-[var(--fg-secondary)]";
        return href ? (
          <a
            key={`${ref.url}:${index}`}
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(event) => event.stopPropagation()}
            className={`${className} hover:bg-[var(--bg-hover)] transition-colors`}
          >
            {content}
          </a>
        ) : (
          <span
            key={`${ref.url}:${index}`}
            className={`${className} opacity-60`}
            title="Evidence URL is unavailable or unsafe"
          >
            {content}
          </span>
        );
      })}
    </div>
  );
}

function SignalList({ icon, label, values }: { icon: React.ReactNode; label: string; values: string[] }) {
  if (values.length === 0) return null;
  return (
    <div>
      <div className="mb-1 flex items-center gap-1 text-[10.5px] font-medium text-[var(--fg-muted)]">{icon}{label}</div>
      <ul className="space-y-1">
        {values.map((value, index) => <li key={index} className="text-[11px] text-[var(--fg-secondary)]">{value}</li>)}
      </ul>
    </div>
  );
}

function PlanRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-[var(--border)] py-2 text-[11.5px] first:pt-0 last:border-b-0 last:pb-0">
      <span className="text-[var(--fg-muted)]">{label}</span>
      <span className="font-medium text-[var(--fg-secondary)]">{value}</span>
    </div>
  );
}

function ContextRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2.5 text-[11.5px] last:border-b-0">
      <span className="text-[var(--fg-muted)]">{icon}</span>
      <span className="flex-1 text-[var(--fg-secondary)]">{label}</span>
      <span className="font-medium text-[var(--fg)]">{value}</span>
    </div>
  );
}

function TimelineIcon({ source, kind }: { source: TimelineEvent["source"]; kind: TimelineEvent["kind"] }) {
  const Icon = source === "git" ? GitCommitHorizontal : source === "agent" ? Bot : kind === "knowledge" ? FileText : ListTodo;
  const tone = source === "git" ? "var(--purple)" : source === "agent" ? "var(--blue)" : "var(--fg-tertiary)";
  return (
    <div
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
      style={{ color: tone, background: `color-mix(in srgb, ${tone} 14%, transparent)` }}
    >
      <Icon size={12} />
    </div>
  );
}

function EmptyPanel({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex min-h-[180px] items-center justify-center rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-6 text-center">
      <div className="max-w-sm">
        <div className="mx-auto mb-3 flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--bg-tertiary)] text-[var(--fg-tertiary)]">{icon}</div>
        <p className="text-[12.5px] font-medium text-[var(--fg)]">{title}</p>
        <p className="mt-1 text-[11.5px] leading-relaxed text-[var(--fg-tertiary)]">{body}</p>
      </div>
    </div>
  );
}

function activityDescription(action: string, entityType: string) {
  const entity = entityDisplayName(entityType);
  if (action === "create") return `${entity} created in this project.`;
  if (action === "status_change") return `${entity} moved to a new status.`;
  if (action === "assign") return `${entity} assignment changed.`;
  if (action === "comment") return `New comment added to this ${entityType}.`;
  if (action === "delete") return `${entity} removed.`;
  return `${entity} updated.`;
}

function compactPrompt(run: AgentRun) {
  const prompt = run.prompt.replace(/\s+/g, " ").trim();
  return prompt.length > 120 ? `${prompt.slice(0, 117)}…` : prompt || "Agent execution";
}

function signalLabel(source: ReturnType<typeof deriveProjectInsights>["latestSignal"]["source"]) {
  if (source === "progress_card") return "progress pulse";
  if (source === "agent_run") return "agent run";
  return source === "issue" ? "task" : source;
}

function entityDisplayName(value: string) {
  return value === "issue" ? "Task" : titleCase(value);
}

function titleCase(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function toDateInput(value: number) {
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function parseDateInput(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).getTime();
}

function startOfDay(value: number) {
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

const HEALTH_TONES: Record<ProjectHealthKind, string> = {
  completed: "var(--green)",
  canceled: "var(--fg-muted)",
  paused: "var(--orange)",
  needs_attention: "var(--red)",
  stale: "var(--yellow)",
  on_track: "var(--green)",
  planned: "var(--blue)",
};
